// src/components/admin/JobForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/admin/ActivityForm.css';
import '../../styles/admin/JobForm.css';
import { API_BASE_URL } from '../../config';
import RichTextEditor from './RichTextEditor';
import { sanitizeHtml, isHtmlEmpty } from '../../utils/html';

interface Job {
  title: string;
  location: string;
  employmentType: 'full-time' | 'part-time' | 'contract' | 'internship' | 'volunteer';
  salaryRange: string;
  description: string;
  responsibilities: string;
  requirements: string;
  applicationEmail: string;
  deadline: string;
  status: 'draft' | 'published';
}

const JobForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isPreview, setIsPreview] = useState<boolean>(false);

  const [job, setJob] = useState<Job>({
    title: '',
    location: '',
    employmentType: 'full-time',
    salaryRange: '',
    description: '',
    responsibilities: '',
    requirements: '',
    applicationEmail: '',
    deadline: '',
    status: 'draft',
  });

  const isMounted = useRef(true);

  // Format date to YYYY-MM-DD for input type="date"
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

  useEffect(() => {
    // Reset on every mount — React StrictMode mounts twice in dev (mount→unmount→mount),
    // and without this reset the ref stays false, leaving the loading spinner stuck.
    isMounted.current = true;
    if (id) {
      fetchJob();
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/jobs/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch job');
      }

      const data = await response.json();

      setJob({
        title: data.title || '',
        location: data.location || '',
        employmentType: data.employmentType || 'full-time',
        salaryRange: data.salaryRange || '',
        description: data.description || '',
        responsibilities: data.responsibilities || '',
        requirements: data.requirements || '',
        applicationEmail: data.applicationEmail || '',
        deadline: formatDateForInput(data.deadline),
        status: data.status || 'draft',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setJob(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (publish: boolean = false) => {
    if (isHtmlEmpty(job.description)) {
      setError('Job description is required.');
      return;
    }
    try {
      setLoading(true);
      setError('');

      const method = id ? 'PUT' : 'POST';
      const url = id ? `${API_BASE_URL}/api/jobs/${id}` : `${API_BASE_URL}/api/jobs`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...job,
          deadline: job.deadline || null,
          status: publish ? 'published' : job.status,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save job');
      }

      navigate('/admin/jobs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save job');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const employmentTypeLabel = (type: string) =>
    type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');

  const renderLines = (text: string) =>
    text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

  if (loading && id) {
    return (
      <div className="activity-form-loading">
        <div className="loading-spinner"></div>
        <p>Loading job...</p>
      </div>
    );
  }

  return (
    <div className="activity-form">
      <div className="activity-form-header">
        <h1>{id ? 'Edit Job Vacancy' : 'Create New Job Vacancy'}</h1>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="form-group">
          <label htmlFor="title">Job Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={job.title}
            onChange={handleInputChange}
            placeholder="e.g. Programs Coordinator"
            className="form-control"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="employmentType">Employment Type</label>
            <select
              id="employmentType"
              name="employmentType"
              value={job.employmentType}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
              <option value="volunteer">Volunteer</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={job.location}
              onChange={handleInputChange}
              placeholder="e.g. Yangon, Myanmar (or Remote)"
              className="form-control"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="salaryRange">Salary / Stipend Range <span className="optional">(optional)</span></label>
            <input
              type="text"
              id="salaryRange"
              name="salaryRange"
              value={job.salaryRange}
              onChange={handleInputChange}
              placeholder="e.g. MMK 800,000 - 1,200,000 / month"
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="deadline">Application Deadline <span className="optional">(optional)</span></label>
            <input
              type="date"
              id="deadline"
              name="deadline"
              value={job.deadline}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="applicationEmail">Application Email</label>
          <input
            type="email"
            id="applicationEmail"
            name="applicationEmail"
            value={job.applicationEmail}
            onChange={handleInputChange}
            placeholder="careers@empowerfuture21.org"
            className="form-control"
            required
          />
          <small className="field-hint">Applicants will send their CVs to this address.</small>
        </div>

        <div className="form-group">
          <label htmlFor="description">Job Description</label>
          <RichTextEditor
            value={job.description}
            onChange={(html) => setJob(prev => ({ ...prev, description: html }))}
            placeholder="Describe the role, the team, and what the position is about..."
          />
        </div>

        <div className="form-group">
          <label htmlFor="responsibilities">Responsibilities <span className="optional">(one per line)</span></label>
          <textarea
            id="responsibilities"
            name="responsibilities"
            value={job.responsibilities}
            onChange={handleInputChange}
            placeholder={'Coordinate community programs\nManage volunteer schedules\nReport on program outcomes'}
            className="form-control job-textarea"
            rows={5}
          />
        </div>

        <div className="form-group">
          <label htmlFor="requirements">Requirements <span className="optional">(one per line)</span></label>
          <textarea
            id="requirements"
            name="requirements"
            value={job.requirements}
            onChange={handleInputChange}
            placeholder={'Bachelor’s degree in a related field\n2+ years of relevant experience\nStrong communication skills'}
            className="form-control job-textarea"
            rows={5}
          />
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            name="status"
            value={job.status}
            onChange={handleInputChange}
            className="form-control"
            disabled={loading}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <div className="preview-toggle">
          <button
            type="button"
            onClick={() => setIsPreview(!isPreview)}
            className="preview-btn"
            disabled={loading}
          >
            {isPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>

        {isPreview && (
          <div className="preview-content">
            <h2>{job.title || 'Untitled Job'}</h2>
            <div className="preview-meta">
              <span>💼 {employmentTypeLabel(job.employmentType)}</span>
              <span>📍 {job.location || 'Location not set'}</span>
              {job.salaryRange && <span>💰 {job.salaryRange}</span>}
              {job.deadline && <span>⏰ Apply by {new Date(job.deadline).toLocaleDateString()}</span>}
            </div>

            {!isHtmlEmpty(job.description) && (
              <div className="preview-block">
                <h3>About the role</h3>
                <div
                  className="rich-text-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.description) }}
                />
              </div>
            )}

            {renderLines(job.responsibilities).length > 0 && (
              <div className="preview-block">
                <h3>Responsibilities</h3>
                <ul>
                  {renderLines(job.responsibilities).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </div>
            )}

            {renderLines(job.requirements).length > 0 && (
              <div className="preview-block">
                <h3>Requirements</h3>
                <ul>
                  {renderLines(job.requirements).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              </div>
            )}

            {job.applicationEmail && (
              <div className="preview-block">
                <h3>How to apply</h3>
                <p>Send your CV to <strong>{job.applicationEmail}</strong></p>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-save"
            disabled={loading}
          >
            {loading ? 'Saving...' : id ? 'Update as Draft' : 'Save as Draft'}
          </button>
          {(job.status === 'draft' || !id) && (
            <button
              type="button"
              className="btn btn-publish"
              onClick={() => handleSubmit(true)}
              disabled={loading}
            >
              {loading ? 'Publishing...' : id ? 'Update & Publish' : 'Save & Publish'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-cancel"
            onClick={() => navigate('/admin/jobs')}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;
